import { useState } from 'react';
import {
  IconChevronDown,
  IconChevronRight,
  IconChevronUp,
  IconDotsVertical,
} from '@rossum/ui/icons/tabler';
import {
  alpha,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  SvgIcon,
  Switch,
  Tooltip,
} from '@rossum/ui/material';
import { useComponentsContext } from '../ComponentsContext';
import {
  TileContextMenuItem,
  TileContextMenuNestedSection,
  TileExpandable,
  TileProps,
  TileQuickAction,
  TileToggleAction,
} from './types';

export const TILE_NAVIGATE_CLASSNAME = 'Tile-navigate';

type TileActionsProps = {
  quickActions?: TileQuickAction[];
  toggleAction?: TileToggleAction;
  contextMenuItems?: TileContextMenuItem[];
  contextMenuNestedSection?: TileContextMenuNestedSection;
  to?: TileProps['to'];
  expandable?: TileExpandable;
};

export const TileActions = ({
  quickActions,
  toggleAction,
  contextMenuItems,
  contextMenuNestedSection,
  to,
  expandable,
}: TileActionsProps) => {
  const { LinkComponent } = useComponentsContext();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const menuOpen = anchorEl !== null;

  const isValid =
    quickActions?.length ||
    toggleAction ||
    contextMenuItems ||
    to ||
    expandable;

  if (!isValid) {
    return null;
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const renderMenuItem = (item: TileContextMenuItem) => {
    const IconComponent = item.Icon;

    return (
      <MenuItem
        key={item.key}
        disabled={item.disabled}
        data-cy={item['data-cy']}
        onClick={() => {
          item.onClick?.();
          handleMenuClose();
        }}
        {...(item.to && {
          component: LinkComponent,
          to: item.to,
        })}
      >
        {IconComponent && (
          <ListItemIcon>
            <SvgIcon>
              <IconComponent />
            </SvgIcon>
          </ListItemIcon>
        )}
        <ListItemText slotProps={{ primary: { variant: 'body2' } }}>
          {item.label}
        </ListItemText>
      </MenuItem>
    );
  };

  const renderContextMenu = () => {
    if (!contextMenuItems) return null;

    const hasItems =
      contextMenuItems.length || contextMenuNestedSection?.items?.length;

    return (
      <>
        <IconButton
          onClick={
            hasItems
              ? e => {
                  e.stopPropagation();
                  handleMenuOpen(e);
                }
              : undefined
          }
          data-cy="tile-context-menu-button"
        >
          <SvgIcon
            sx={{
              color: theme => alpha(theme.palette.action.active, 0.56),
            }}
          >
            <IconDotsVertical />
          </SvgIcon>
        </IconButton>
        {hasItems && (
          <Menu open={menuOpen} onClose={handleMenuClose} anchorEl={anchorEl}>
            {/* Main items */}
            {contextMenuItems.map(renderMenuItem)}

            {/* Nested section (edge case) */}
            {contextMenuNestedSection &&
              contextMenuNestedSection.items.length > 0 && [
                <Divider key="nested-divider" />,
                contextMenuNestedSection.header && (
                  <ListSubheader
                    key="nested-header"
                    sx={{ backgroundColor: 'transparent', py: 0, my: 0 }}
                  >
                    {contextMenuNestedSection.header}
                  </ListSubheader>
                ),
                ...contextMenuNestedSection.items.map(renderMenuItem),
              ]}
          </Menu>
        )}
      </>
    );
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1} flexShrink={0}>
      {/* Quick action buttons */}
      {quickActions?.map(action => {
        const { Icon, state, disabled } = action.iconProps;
        return (
          <Tooltip
            key={action.key}
            title={action.disabled ? '' : (action.tooltip ?? '')}
          >
            <span onClick={e => e.stopPropagation()}>
              <IconButton
                data-cy={action['data-cy']}
                disabled={action.disabled}
                onClick={() => action.onClick()}
              >
                <SvgIcon
                  color={disabled ? 'disabled' : state ? 'success' : 'action'}
                >
                  <Icon />
                </SvgIcon>
              </IconButton>
            </span>
          </Tooltip>
        );
      })}

      {/* Toggle switch */}
      {toggleAction && (
        <Tooltip
          title={toggleAction.disabled ? '' : (toggleAction.tooltip ?? '')}
        >
          <span onClick={e => e.stopPropagation()}>
            <Switch
              checked={toggleAction.checked}
              disabled={toggleAction.disabled}
              onChange={toggleAction.onChange}
              data-cy={toggleAction['data-cy']}
            />
          </span>
        </Tooltip>
      )}

      {/* Context menu */}
      {renderContextMenu()}

      {/* Navigation chevron */}
      {to && !expandable && (
        <IconButton
          className={TILE_NAVIGATE_CLASSNAME}
          component={LinkComponent}
          to={to}
          sx={{
            opacity: 0,
            transition: t =>
              t.transitions.create('opacity', {
                duration: t.transitions.duration.short,
              }),
          }}
        >
          <SvgIcon color="action">
            <IconChevronRight />
          </SvgIcon>
        </IconButton>
      )}

      {/* Expand/collapse chevron */}
      {expandable && (
        <IconButton
          onClick={e => {
            e.stopPropagation();
            expandable.onToggleExpanded();
          }}
        >
          <SvgIcon color="action">
            {expandable.expanded ? <IconChevronUp /> : <IconChevronDown />}
          </SvgIcon>
        </IconButton>
      )}
    </Stack>
  );
};
